import * as React from 'react';
import { IRestriction, IDepot } from '../../models';
import { useDepots } from '../../hooks/useDepots';
import { useData } from '../../core/context/ProvidersContext';
import { getAllProducts } from '../shared/ProductColors';
import styles from './NewRestrictionModal.module.scss';

export interface INewRestrictionModalProps {
  onCreated: () => void;
  onCancel: () => void;
}

interface IFormState {
  depotId: number;
  produit: string;
  dateDebut: string;
  dateFin: string;
  restrictionEstimee: string;
  remarques: string;
}

const EMPTY_FORM: IFormState = {
  depotId: 0,
  produit: '',
  dateDebut: '',
  dateFin: '',
  restrictionEstimee: '',
  remarques: '',
};

export const NewRestrictionModal: React.FC<INewRestrictionModalProps> = ({
  onCreated, onCancel,
}) => {
  const { depots, loading: depotsLoading } = useDepots();
  const data = useData();

  const [form, setForm] = React.useState<IFormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const allProducts = React.useMemo(() => getAllProducts(), []);

  const selectedDepot: IDepot | undefined = depots.find(d => d.Id === form.depotId);

  const validate = (): string | null => {
    if (!form.depotId) return 'Le dépôt est obligatoire';
    if (!form.produit) return 'Le produit est obligatoire';
    if (!form.dateDebut) return 'La date de début est obligatoire';
    if (!form.dateFin) return 'La date de fin est obligatoire';
    if (new Date(form.dateFin) < new Date(form.dateDebut)) {
      return 'La date de fin doit être après la date de début';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSaving(true);
    try {
      const title = `${selectedDepot?.Title || ''}-${form.produit}`;
      const payload: Omit<IRestriction, 'Id' | 'DepotTitle'> = {
        Title: title,
        DepotId: form.depotId,
        Produit: form.produit,
        RestrictionEstimee: form.restrictionEstimee,
        DateDebut: form.dateDebut,
        DateFin: form.dateFin,
        Remarques: form.remarques,
      };
      await data.addRestriction(payload);
      onCreated();
    } catch (e) {
      setError((e as Error).message || 'Erreur lors de la création');
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <form className={styles.modal} onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.headerIcon}>+</span>
            <div>
              <h3 className={styles.headerTitle}>Nouvelle restriction</h3>
              <p className={styles.headerSubtitle}>Limitation de disponibilité pour les clients</p>
            </div>
          </div>
          <button type="button" className={styles.close} onClick={onCancel}>×</button>
        </div>

        <div className={styles.body}>
          {depotsLoading ? (
            <div className={styles.loading}>Chargement des dépôts…</div>
          ) : (
            <>
              <div className={styles.row}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Dépôt <em>*</em></span>
                  <select
                    className={styles.input}
                    value={form.depotId}
                    onChange={e => setForm({ ...form, depotId: Number(e.target.value) })}
                    required
                  >
                    <option value={0}>— Sélectionner —</option>
                    {depots.map(d => (
                      <option key={d.Id} value={d.Id}>{d.Title}</option>
                    ))}
                  </select>
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Produit <em>*</em></span>
                  <select
                    className={styles.input}
                    value={form.produit}
                    onChange={e => setForm({ ...form, produit: e.target.value })}
                    required
                  >
                    <option value="">— Sélectionner —</option>
                    {allProducts.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className={styles.row}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Date de début <em>*</em></span>
                  <input
                    type="date"
                    className={styles.input}
                    value={form.dateDebut}
                    onChange={e => setForm({ ...form, dateDebut: e.target.value })}
                    required
                  />
                </label>

                <label className={styles.label}>
                  <span className={styles.labelText}>Date de fin <em>*</em></span>
                  <input
                    type="date"
                    className={styles.input}
                    value={form.dateFin}
                    onChange={e => setForm({ ...form, dateFin: e.target.value })}
                    required
                  />
                </label>
              </div>

              <label className={styles.label}>
                <span className={styles.labelText}>Restriction estimée</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="ex : -30% ou texte libre"
                  value={form.restrictionEstimee}
                  onChange={e => setForm({ ...form, restrictionEstimee: e.target.value })}
                />
              </label>

              <label className={styles.label}>
                <span className={styles.labelText}>Remarques</span>
                <textarea
                  className={styles.textarea}
                  rows={3}
                  placeholder="Contexte, décisions, chargé d'affaires…"
                  value={form.remarques}
                  onChange={e => setForm({ ...form, remarques: e.target.value })}
                />
              </label>

              {error && <div className={styles.error}>{error}</div>}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btnGhost} onClick={onCancel} disabled={saving}>
            Annuler
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={saving || depotsLoading}>
            {saving ? 'Création…' : 'Créer la restriction'}
          </button>
        </div>
      </form>
    </div>
  );
};
